
import React, { useState, useEffect } from 'react';
import { Paper } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { DatesRangeValue } from '@mantine/dates';
import { isAfter, isBefore, isSameDay, startOfDay } from "date-fns";

interface MantineCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  handleDateClick: (date: Date) => void;
}

export const MantineCalendar: React.FC<MantineCalendarProps> = ({
  startDate,
  endDate,
  handleDateClick
}) => {
  // Track current visible month
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    // Initialize with startDate if valid, otherwise use today
    return startDate instanceof Date && !isNaN(startDate.getTime()) 
      ? new Date(startDate) 
      : new Date();
  });
  
  // Initialize with an empty array as the safe default for Mantine's range picker
  let rangeValue: DatesRangeValue = [null, null];
  
  // Only create a range value if both dates are valid Date objects
  if (startDate instanceof Date && !isNaN(startDate.getTime())) {
    if (endDate instanceof Date && !isNaN(endDate.getTime())) {
      // Create new Date objects to avoid reference issues
      rangeValue = [new Date(startDate), new Date(endDate)];
    } else {
      // If only start date is valid, make an array with start date and null
      rangeValue = [new Date(startDate), null];
    }
  } else if (endDate instanceof Date && !isNaN(endDate.getTime())) {
    // If only end date is valid (unusual case), set both to end date
    rangeValue = [new Date(endDate), new Date(endDate)];
  }

  // Normalize to local day boundaries so UI works even if stored values
  // use non-midnight times (e.g. UTC-noon for a local day => 8:00 AM ET).
  const startDay =
    startDate instanceof Date && !isNaN(startDate.getTime())
      ? startOfDay(startDate)
      : null;
  const endDay =
    endDate instanceof Date && !isNaN(endDate.getTime())
      ? startOfDay(endDate)
      : null;

  // Update current month when startDate changes significantly
  useEffect(() => {
    if (startDate && !isNaN(startDate.getTime())) {
      setCurrentMonth(new Date(startDate));
    }
  }, [startDate]);
  
  // Handle month change when using next/previous buttons
  const handleMonthChange = (newMonth: Date) => {
    setCurrentMonth(newMonth);
  };
  
  return (
    <Paper p="xs" withBorder>
      <DatePicker
        type="range"
        value={rangeValue}
        defaultDate={currentMonth} 
        date={currentMonth} // Use current month for displayed month
        onDateChange={handleMonthChange} // Handle month navigation
        defaultValue={[new Date(), new Date()]} 
        allowSingleDateInRange={true}
        getDayProps={(date) => ({
          selected:
            (!!startDay && isSameDay(startOfDay(date), startDay)) ||
            (!!endDay && isSameDay(startOfDay(date), endDay)),
          inRange:
            !!startDay &&
            !!endDay &&
            isAfter(startOfDay(date), startDay) &&
            isBefore(startOfDay(date), endDay),
          onClick: () => handleDateClick(date)
        })}
        defaultLevel="month"
        numberOfColumns={2}
      />
    </Paper>
  );
};
